import { useState } from 'react';
import type { CSSProperties, SVGProps } from 'react';
import { VEHICLE_IMAGES } from '@/data/assetManifest';
import { VehicleSilhouette } from './silhouettes';
import type { VehicleType } from '@/state/types';

interface VehicleImageProps {
  type: VehicleType;
  /** Tailwind classes; sets sizing/colour for the silhouette fallback. */
  className?: string;
  style?: CSSProperties;
  alt?: string;
  /** Forwarded to the silhouette when no real asset is available. */
  silhouetteProps?: SVGProps<SVGSVGElement>;
}

/**
 * Phase 12.2.2 — Vehicle image with progressive fallback chain:
 *
 *   1. If `assetManifest.VEHICLE_IMAGES[type]` is set, render that PNG.
 *   2. If the PNG load fails (404 / network), swap to the silhouette.
 *   3. If no asset is configured at all (police vehicles), use silhouette
 *      directly without ever attempting an `<img>` request.
 */
export function VehicleImage({
  type,
  className,
  style,
  alt,
  silhouetteProps,
}: VehicleImageProps) {
  const src = VEHICLE_IMAGES[type];
  const [errored, setErrored] = useState(false);

  if (!src || errored) {
    return (
      <VehicleSilhouette
        className={className}
        style={style}
        {...silhouetteProps}
        type={type}
      />
    );
  }

  return (
    <img
      src={src}
      alt={alt ?? `${type}-Fahrzeug`}
      className={className}
      style={style}
      loading="lazy"
      onError={() => setErrored(true)}
    />
  );
}
